import React, { FC } from 'react';

import { BUTTON_SIZES } from '@buyside/components/presentation/Button/button';
import FollowButton, {
  FollowableType
} from '@buyside/components/presentation/FollowButton/followButton';
import AnonymousUserDisplay from '@buyside/components/presentation/AnonymousUserDisplay/anonymousUserDisplay';
import Badge from '@buyside/components/presentation/Badges/Badge/badge';
import Link from '@buyside/components/presentation/Link/link';
import MessageButton from '@buyside/components/presentation/MessageModal/messageButton';
import RankCardDeltaBadge from '@buyside/components/presentation/RankingsIndexPage/MainContent/RankCard/rankCardDeltaBadge';
import RankCardIdea from '@buyside/components/presentation/RankingsIndexPage/MainContent/RankCard/rankCardIdea';
import RankCardShareButtons from '@buyside/components/presentation/RankingsIndexPage/MainContent/RankCard/rankCardShareButtons';

import { employerProfileUrl, userIdeaUrl, userUrl } from '@buyside/util/urlHelper';

import { RankCardFragment } from '@interfaces/graphql-typings';

import styles from './rankCardStyles.scss';

const RankCard: FC<{
  canViewUserDetails?: boolean;
  isCurrentUser: boolean;
  isPublic: boolean;
  rank: RankCardFragment;
}> = ({ canViewUserDetails = true, isCurrentUser, isPublic, rank }) => {
  const { id, rankString, user, viewDeltaString } = rank;

  const rankStringNumber = rankString.split(' ')[0];
  const rankStringCategory = rankString.slice(rankString.indexOf(' ') + 1);

  const showUserDetails = !isPublic && canViewUserDetails;

  return (
    <div className={styles.rankCardContainer} id={`rank-card-${id}`}>
      <div className={styles.rankCardStart}>
        <div className={styles.rankCardRank}>
          <span>{rankStringNumber}</span>
          <span>{rankStringCategory}</span>
        </div>
        {viewDeltaString ? <RankCardDeltaBadge viewDeltaString={viewDeltaString} /> : null}
      </div>
      <div className={styles.rankCardMiddle}>
        <div className={styles.userDisplay}>
          <div className={styles.userDisplayStart}>
            <img
              className={styles.userDisplayProfileImage}
              src={
                showUserDetails
                  ? user?.profileImageUrl || '/images/shared/avatar-missing-115.png'
                  : '/images/shared/avatar-missing-115.png'
              }
            />
            {showUserDetails ? (
              <RankCardShareButtons
                isCurrentUser={isCurrentUser}
                shareUrl={`https://sumzero.com/?from_rank=${id}`}
                shareText={`${user?.displayName} is the ${rankStringCategory} Analyst on SumZero`}
              />
            ) : null}
          </div>
          <div>
            {showUserDetails ? (
              <>
                <h4>
                  <Link to={userUrl(user?.id || '')}>{user?.displayName}</Link>
                </h4>
                <Link to={employerProfileUrl(user?.mostRecentEmployer?.employer?.id || '')}>
                  {user?.currentEmployerName}
                </Link>
              </>
            ) : (
              <>
                <h4>
                  <AnonymousUserDisplay anonUser={{ title: user?.mostRecentEmployer?.title }} />
                </h4>
                {user?.mostRecentEmployer?.currentEmployer ? (
                  <p>
                    {user?.mostRecentEmployer?.employer?.aumRange}{' '}
                    {user?.mostRecentEmployer?.employer?.fundType}
                  </p>
                ) : null}
              </>
            )}
            <p>{user?.profileCity}</p>
            {user && !isCurrentUser && showUserDetails ? (
              <div className={styles.userDisplayActions}>
                <FollowButton type={FollowableType.USER} id={user.id} size={BUTTON_SIZES.TINY} />
                <MessageButton
                  isCurrentEmployer={user.mostRecentEmployer?.currentEmployer}
                  receiverEmployerName={user.currentEmployerName || ''}
                  receiverId={user.id}
                  receiverName={user.displayName}
                  showUpsell
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className={styles.rankCardEnd}>
        <div className={styles.topIdeasListHeader}>
          <label>Top Ideas</label>
          {showUserDetails ? (
            <label>
              <Link to={userIdeaUrl(user?.id || '')}>View All Ideas</Link>
            </label>
          ) : null}
        </div>
        <div className={styles.ideasList}>
          {user?.topIdeas?.edges?.map(edge =>
            edge?.node ? <RankCardIdea key={`top-idea-${edge?.node?.id}`} {...edge.node} /> : null
          )}
        </div>
        {user?.recentIdea ? (
          <div className={styles.latestIdeaAndBadges}>
            <div>
              <label>Latest Idea</label>
              <div className={styles.ideasList}>
                <RankCardIdea key={`latest-idea-${user.recentIdea.id}`} {...user.recentIdea} />
              </div>
            </div>
            {user?.badgeCollectionRelationships?.edges?.length ? (
              <div>
                <label>Badges</label>
                <div className={styles.badgesList}>
                  {[...(user?.badgeCollectionRelationships?.edges || [])]
                    .reverse()
                    .map(edge =>
                      edge?.node?.visible ? (
                        <Badge key={`badge-${edge.node.id}`} badgeInfo={edge.node} />
                      ) : null
                    )}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default RankCard;
